import React from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';
import { Toast } from '@/hooks/useToast.ts';

interface ToastComponentProps {
    toast: Toast;
    onRemove: (id: string) => void;
}

export const ToastComponent: React.FC<ToastComponentProps> = ({ toast, onRemove }) => {
    const getIcon = () => {
        switch (toast.type) {
            case 'success':
                return <CheckCircle className="h-5 w-5 text-green-400" />;
            case 'error':
                return <AlertCircle className="h-5 w-5 text-red-400" />;
            case 'warning':
                return <AlertCircle className="h-5 w-5 text-yellow-400" />;
            case 'info':
                return <Info className="h-5 w-5 text-blue-400" />;
            default:
                return <Info className="h-5 w-5 text-blue-400" />;
        }
    };

    const getStyles = () => {
        switch (toast.type) {
            case 'success':
                return {
                    container: 'bg-green-50 border border-green-200',
                    title: 'text-green-800',
                    message: 'text-green-700',
                    button: 'text-green-500 hover:bg-green-100 focus:ring-green-600'
                };
            case 'error':
                return {
                    container: 'bg-red-50 border border-red-200',
                    title: 'text-red-800',
                    message: 'text-red-700',
                    button: 'text-red-500 hover:bg-red-100 focus:ring-red-600'
                };
            case 'warning':
                return {
                    container: 'bg-yellow-50 border border-yellow-200',
                    title: 'text-yellow-800',
                    message: 'text-yellow-700',
                    button: 'text-yellow-500 hover:bg-yellow-100 focus:ring-yellow-600'
                };
            case 'info':
                return {
                    container: 'bg-blue-50 border border-blue-200',
                    title: 'text-blue-800',
                    message: 'text-blue-700',
                    button: 'text-blue-500 hover:bg-blue-100 focus:ring-blue-600'
                };
            default:
                return {
                    container: 'bg-gray-50 border border-gray-200',
                    title: 'text-gray-800',
                    message: 'text-gray-700',
                    button: 'text-gray-500 hover:bg-gray-100 focus:ring-gray-600'
                };
        }
    };

    const styles = getStyles();

    return (
        <div className={`max-w-sm rounded-lg shadow-lg p-4 transition-all duration-300 transform animate-slide-in ${styles.container}`}>
            <div className="flex items-start">
                <div className="flex-shrink-0">
                    {getIcon()}
                </div>
                <div className="ml-3 flex-1">
                    <p className={`text-sm font-medium ${styles.title}`}>
                        {toast.title}
                    </p>
                    <p className={`mt-1 text-sm ${styles.message}`}>
                        {toast.message}
                    </p>
                </div>
                <div className="ml-4 flex-shrink-0 flex">
                    <button
                        className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${styles.button}`}
                        onClick={() => onRemove(toast.id)}
                        aria-label="Close notification"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};